import React, { useEffect, useState } from 'react';
import upperFirst from 'lodash/upperFirst';
import CMSArticle from 'src/shared/components/cms-article/CMSArticle';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, DataTableState } from 'src/shared/components/data-table/dataTableTypes';
import { dataTableDefaultDownloadHandler } from 'src/shared/components/data-table/helpers/downloadHandler';
import DownloadButton from 'src/shared/components/download-button/DownloadButton';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import HabitatAssayLozenges from 'src/shared/components/lozenges/HabitatAssayLozenges';
import useProject from 'src/shared/hooks/useProject';
import useProjectBreadcrumb from 'src/shared/hooks/useProjectBreadcrumb';
import { AppName } from 'src/global/globalConfig';
import { formatStandardDate, formatCompactDate } from 'src/shared/utils/dateHelpers';
import { getEventIndexAndName } from 'src/app/tables/sample-metrics-table/SampleMetricsTable.utils';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import DemoProjectBanner from 'src/shared/components/demo-project-banner/DemoProjectBanner';
import styles from './SampleMetricsTable.module.scss';
import { useSampleMetricsTableDataQuery } from '../state/api/tablesGraphSlice';

const PAGE_TITLE = 'Sample metrics table';
export const SampleMetricsTable = () => {
    const { currentProjectDetails } = useProject();

    const {
        currentProjectId,
        currentProjectFilters,
        currentProjectSelectedHabitatAssayType: habitatAssayType,
        setCurrentProjectHabitatAssayType,
        samplingEventFilterData,
    } = useProjectFilters();

    const currentProjectBreadcrumbDetails = useProjectBreadcrumb(PAGE_TITLE);
    const reportDate = formatStandardDate(currentProjectDetails?.latestReportingDate);
    const [columns, setColumns] = useState<DataTableColumns>([]);
    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
    });

    const { currentData, isFetching } = useSampleMetricsTableDataQuery(
        {
            projectId: currentProjectId || '',
            samples: {
                habitatAssay: [habitatAssayType?.key || ''],
            },
        },
        {
            skip: !currentProjectId || !habitatAssayType,
        }
    );

    useEffect(() => {
        if (!currentData || !currentProjectFilters) {
            return;
        }

        const { habitatAssays } = currentProjectFilters;

        const {
            sampleMetricsTableData: { samples },
        } = currentData;

        const metricColumns: DataTableColumns = [];

        const habitatAssay = habitatAssays.find(entry => entry.key === habitatAssayType?.key);

        if (!habitatAssay) {
            return;
        }

        const { metrics, assay } = habitatAssay;

        const { assayId } = assay;

        metrics.forEach(metric => {
            metricColumns.push({
                columnId: `${assayId}-${metric.metricKey}`,
                title: metric.metricName,
            });
        });

        const columnsAndIndex: { [key: string]: number } = {};

        metricColumns.forEach((entry, index) => {
            columnsAndIndex[entry.columnId] = index + 1;
        });
        const totalColumns = metricColumns.length + 4;
        const tableData: DataTableState['data'] = [];

        samples.forEach(sample => {
            const { metrics, sampleId, location, covariates, sampleCollectedDatetime } = sample;
            const collectedDateStr = sampleCollectedDatetime ? formatStandardDate(sampleCollectedDatetime) : '';
            const eventDetails = sampleCollectedDatetime
                ? getEventIndexAndName(sampleCollectedDatetime, samplingEventFilterData || [])
                : null;
            const currentRow = Array(totalColumns).fill('');
            currentRow[0] = sampleId;
            metrics.forEach(metric => {
                const columnId = `${metric.assayId}-${metric.metricKey}`;
                currentRow[columnsAndIndex[columnId]] = metric.metricValue;
            });
            currentRow[totalColumns - 3] = collectedDateStr;
            currentRow[totalColumns - 2] = covariates[0].covariateValue;
            currentRow[totalColumns - 1] = location.coordinates.join(', ');
            currentRow[totalColumns] = eventDetails?.event !== undefined ? `Year ${eventDetails?.year}, ${eventDetails?.name}` : 'N/A';
            tableData.push(currentRow);
        });
        setColumns([
            {
                columnId: 'sampleId',
                title: 'Sample ID',
                width: '120px',
            },
            ...metricColumns,
            {
                columnId: 'sampleCollectedDate',
                title: 'Sample Collected Date',
                width: '120px',
            },
            {
                columnId: 'covariates',
                title: 'Sample Group',
                width: 'auto',
            },
            {
                columnId: 'location',
                title: 'Latitude Longitude',
                width: 'auto',
            },
            {
                columnId: 'samplingEvent',
                title: 'Sampling Event',
                width: 'auto',
            },
        ]);

        setTableState({
            ...tableState,
            data: tableData,
        });
    }, [currentData, habitatAssayType]);

    const onHabitatAssayTypeChange = (value: string) => {
        // Reset table state
        setTableState({});
        setColumns([]);
        setCurrentProjectHabitatAssayType(value);
    };

    if (isFetching) {
        return (
            <>
                <Loader />
            </>
        );
    }

    if (!currentProjectDetails || !currentData || !tableState.data) {
        return null;
    }

    const handleDownload = () => {
        if (!tableState.data) {
            return;
        }

        const downloadFileName = [
            'NM-',
            currentProjectId,
            '.',
            upperFirst(habitatAssayType?.habitat.habitatName),
            '.',
            upperFirst(habitatAssayType?.assay.assayName),
            '.Metrics.',
            formatCompactDate(currentProjectDetails?.latestReportingDate),
            '.csv',
        ].join('');

        dataTableDefaultDownloadHandler({
            data: tableState.data,
            columns,
            downloadFileName,
        });
    };
    const otherActions = (
        <div className={styles.otherActions}>
            <div className={styles.reportDate}>
                <span className={styles.reportDateLabel}>Report Date:</span>
                <span className={styles.reportDateValue}>{reportDate}</span>
            </div>
            <DownloadButton onClick={handleDownload} className={styles.downloadButton} />
        </div>
    );

    const mainContent = (
        <div className={styles.tableWrapper}>
            <DataTable state={tableState} columns={columns} onStateChange={setTableState} className={styles.dataTable} />
        </div>
    );

    const lozenges = (
        <HabitatAssayLozenges
            options={currentProjectFilters?.habitatAssayTypeOptions || []}
            selectedValue={habitatAssayType?.key || ''}
            onChange={onHabitatAssayTypeChange}
            app={AppName.SAMPLE_METRICS_TABLE}
        />
    );

    return (
        <StandardAppLayout
            lozenges={lozenges}
            mainContent={mainContent}
            otherActions={otherActions}
            title={PAGE_TITLE}
            subTitle={<CMSArticle slug='sample-metrics-table-description' />}
            breadcrumbs={currentProjectBreadcrumbDetails}
            topBanner={<DemoProjectBanner pageName={AppName.SAMPLE_METRICS_TABLE} />}
        />
    );
};

export default SampleMetricsTable;
